/* Getting the app back on its feet after a render crash.

   React unmounts the whole tree when a render throws and nothing catches it, and what the player
   sees is a black page with no button on it. `components/CrashScreen` catches it; this is what
   it does about it, kept pure enough to test.

   The first crash in a session is answered by itself: every per-browser preference is cleared
   (they are the only state a fresh install does not have, and a fresh install is what "works in
   incognito" means) and the page reloads, once — `sessionStorage` remembers the build it did that
   for, so a crash that survives the reload is shown rather than looped on. The session tokens are
   kept: a player thrown out of their account by a bad preference has been punished twice. The
   second crash gets the screen with the two buttons, and "start clean" is the whole reset —
   tokens, preferences, the worker's caches — which is what an incognito window gave the owner on
   2026-09-24, offered without having to know what an incognito window is. */

import { STORAGE_KEY, loadFromLocalStorage } from "src/localStorage"

// Every per-browser preference this client writes carries this prefix — the language, the
// shortcuts, the audio, the fog preview, the grid, the radar, the install prompt's
// "not now". A new one should too, or a reset will not reach it.
export const CLIENT_STATE_PREFIX = "hvz-"
// The one preference kept through an automatic recovery: a Spanish player who crashed and came
// back in English would read that as a second bug. It cannot be corrupt in a way that crashes —
// i18next only ever resolves it to a supported language.
const KEPT_ON_RECOVERY = new Set(["hvz-lang"])
export const RECOVERED_KEY = "hvz-crash-recovered"

const storageKeys = (storage) => {
  const keys = []
  for (let index = 0; index < storage.length; index += 1) keys.push(storage.key(index))
  return keys
}

/** Drops this browser's client state. With `keepSession`, the tokens stay and the player stays
 *  signed in; without it, everything goes. Never throws: storage the browser refuses is storage
 *  with nothing in it to clear. */
export const clearClientState = ({ keepSession = true } = {}) => {
  try {
    const local = window.localStorage
    storageKeys(local)
      .filter((key) => key.startsWith(CLIENT_STATE_PREFIX) && !(keepSession && KEPT_ON_RECOVERY.has(key)))
      .forEach((key) => local.removeItem(key))
    if (keepSession) {
      const { accessToken, refreshToken } = loadFromLocalStorage()
      const session = {}
      if (accessToken) session.accessToken = accessToken
      if (refreshToken) session.refreshToken = refreshToken
      local.setItem(STORAGE_KEY, JSON.stringify(session))
    } else {
      local.removeItem(STORAGE_KEY)
    }
  } catch (error) {
    // Private mode, or a browser that refuses storage: there was nothing stored to be corrupt.
  }
}

/** Forgets the installed shell and bundle, so the next load is served fresh. Best effort. */
export const forgetInstalledApp = async () => {
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
    if (typeof caches !== "undefined") {
      const names = await caches.keys()
      await Promise.all(names.map((name) => caches.delete(name)))
    }
  } catch (error) {
    // A worker that will not go is a worker the next deploy replaces; nothing to do here.
  }
}

/** The one automatic recovery per build per session. Answers whether it reloaded. */
export const recoverOnce = ({ buildId = null, session = null, reload = null } = {}) => {
  const stamp = buildId ?? ""
  try {
    const store = session ?? window.sessionStorage
    if (store.getItem(RECOVERED_KEY) === stamp) return false
    store.setItem(RECOVERED_KEY, stamp)
  } catch (error) {
    // No session storage means no way to bound the loop, so no automatic reload: the screen it
    // falls through to has a button, and a button cannot loop.
    return false
  }
  clearClientState({ keepSession: true })
  ;(reload ?? (() => window.location.reload()))()
  return true
}
